/**
 * Displays a toast notification based on the given intent and message.
 *
 * @param {object} toast - The toast object used to display notifications.
 * @param {string} intent - The intent of the toast ('success', 'error', 'info', 'warning').
 * @param {string} message - The message to display in the toast notification.
 * @throws {Error} Throws an error if an invalid intent is provided or there is an issue displaying the toast.
 */
export const showToast = (toast, intent, message) => {
  try {
    // Ensure the intent is a valid type
    if (typeof intent !== 'string' || !message) {
      throw new Error(
        'Invalid input: intent must be a string and message is required.'
      );
    }

    // Determine which type of toast to show based on the intent
    switch (intent.toLowerCase()) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'info':
        toast.info(message);
        break;
      case 'warning':
        toast.warning(message);
        break;
      default:
        toast(message);
    }
  } catch (error) {
    console.error('Error occurred while showing toast: ', error.message);
    throw new Error(`Failed to show toast: ${error.message}`);
  }
};

export const validateConfirmPassword = async (
  password,
  confirmPassword,
  setErrors
) => {
  if (confirmPassword !== password) {
    setErrors((prevErrors) => ({
      ...prevErrors,
      confirmPassword: 'Passwords must match!'
    }));
  } else {
    setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: '' }));
  }
};

/**
 * Converts a string to title case format, handling special cases
 * @param {string} str - The string to convert
 * @returns {string} The converted string in title case
 */
export const toTitleCase = (str) => {
  if (!str) return '';

  // Handle hyphenated words and multiple spaces
  return str
    .toLowerCase()
    .split(/[\s-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Converts a preFilledData object with snake_case keys into a structured initialFormData object
 * with camelCase keys for use in the profile sections.
 *
 * The returned object has the following structure:
 * {
 *   profilePicture: null,
 *   personalInfo: {
 *     firstName, lastName, dateOfBirth, gender
 *   },
 *   contactInfo: {
 *     phoneNumber, alternatePhoneNumber, country, state, city, streetAddress, postalCode
 *   },
 *   emergencyContact: {
 *     emergencyContactName, emergencyContactRelationship, emergencyContactPhone, emergencyContactEmail
 *   },
 *   medicalInfo: {
 *     bloodGroup, knownAllergies, chronicConditions, currentMedications, primaryPhysician
 *   },
 *   preferences: {
 *     preferredLanguage, notificationPreferences
 *   },
 *   insuranceInfo: {
 *     insuranceName, insuranceNumber, insuranceExpiry
 *   }
 * }
 *
 * @param {Object} preFilledData - The object containing prefilled data with snake_case property names.
 * @returns {Object} initialFormData - The structured data with camelCase keys.
 */
export const convertToInitialFormData = (preFilledData) => {
  return {
    profilePicture: null,
    personalInfo: {
      firstName: preFilledData.first_name || '',
      lastName: preFilledData.last_name || '',
      dateOfBirth: preFilledData.date_of_birth || '',
      gender: preFilledData.gender || ''
    },
    contactInfo: {
      phoneNumber: preFilledData.phone_number || '',
      alternatePhoneNumber: preFilledData.alternate_phone_number || '',
      country: preFilledData.country || '',
      state: preFilledData.state_province || '',
      city: preFilledData.city || '',
      streetAddress: preFilledData.street_address || '',
      postalCode: preFilledData.postal_code || ''
    },
    emergencyContact: {
      emergencyContactName: preFilledData.emergency_contact_name || '',
      emergencyContactRelationship:
				preFilledData.emergency_contact_relationship || '',
      emergencyContactPhone: preFilledData.emergency_contact_phone || '',
      emergencyContactEmail: preFilledData.emergency_contact_email || ''
    },
    medicalInfo: {
      bloodGroup: preFilledData.blood_group || '',
      knownAllergies: preFilledData.known_allergies || [],
      chronicConditions: preFilledData.chronic_conditions || [],
      currentMedications: preFilledData.current_medications || '',
      primaryPhysician: preFilledData.primary_physician || ''
    },
    preferences: {
      preferredLanguage: preFilledData.preferred_language || '',
      notificationPreferences: preFilledData.notification_preferences || ''
    },
    insuranceInfo: {
      insuranceName: preFilledData.insurance_name || '',
      insuranceNumber: preFilledData.insurance_number || '',
      insuranceExpiry: preFilledData.insurance_expiry || ''
    }
  };
};

/**
 * Calculates age from a given date of birth string in "YYYY-MM-DD" format.
 * @param {string} dateOfBirth - The date of birth (e.g., "2025-01-28").
 * @returns {number} - The calculated age.
 */
export const calculateAge = (dateOfBirth) => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  // If the birthday hasn't occurred yet this year, subtract 1.
  if (
    monthDifference < 0 ||
		(monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};
